import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { usePageMeta } from "@/hooks/usePageMeta";

const VALUES = [
  { icon: "ri-compass-3-line", title: "信息清晰", desc: "分类、筛选、详情一层层展开，先判断值不值得看。" },
  { icon: "ri-links-line", title: "互相连通", desc: "每部作品、每篇资讯都与分类页双向链接，没有孤岛页面。" },
  { icon: "ri-shield-check-line", title: "内容诚实", desc: "不提供在线播放，不虚构评分，全部内容标注为示例。" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们-奇优影院",
    "奇优影院是面向影视爱好者的资讯与推荐平台，提供电影、电视剧分类浏览、作品详情与行业资讯，本页介绍平台定位、联系方式与使用声明。",
    "关于奇优影院,影视资讯平台,联系我们,免责声明",
  );

  return (
    <SiteLayout>
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-5 md:pt-7">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="rounded-lg bg-primary-950 px-5 py-8 md:px-10 md:py-12">
          <p className="text-[11px] tracking-[0.22em] text-accent-400 font-label uppercase mb-2">About Us</p>
          <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 font-heading leading-tight text-balance">
            关于奇优影院
          </h1>
          <p className="text-sm md:text-[15px] text-background-300/90 mt-3 max-w-2xl leading-relaxed">
            我们把散落在各处的新片消息、剧集信息与行业动态整理成一份清晰可查的影视地图，让你在按下播放键之前，就已经知道自己在等什么。
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <Link
              to="/category/movie"
              className="h-10 px-4 inline-flex items-center rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览电影分类
            </Link>
            <Link
              to="/category/news"
              className="h-10 px-4 inline-flex items-center rounded-md border border-primary-700 text-sm text-background-100 hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看影视资讯
            </Link>
          </div>
        </header>

        <section className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <h2 className="text-lg font-bold text-foreground-950 font-heading mb-2.5">平台定位</h2>
            <div className="space-y-3 text-sm md:text-[15px] text-foreground-700 leading-[1.9]">
              <p>
                奇优影院是一个影视资讯与推荐平台，面向喜欢电影与电视剧的普通观众。我们不提供在线播放、下载或票务服务，只做一件事：把作品信息整理清楚。
              </p>
              <p>
                站内的电影与剧集按类型、地区、年份分类，每部作品都有独立的详情页，包含剧情概要、主创信息与（剧集）分集目录；资讯栏目则记录定档、预告、幕后与行业动态。
              </p>
              <p>
                为了让你少走弯路，搜索支持按片名、类型、导演与演员检索，筛选栏支持多条件组合，收藏功能可以把你感兴趣的作品留在本机，方便下次继续看。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-secondary-50 p-4 md:p-5">
            <h2 className="text-sm font-bold text-foreground-950 font-heading mb-2.5">联系方式</h2>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-2">
                <i className="ri-mail-line text-base text-primary-600 w-4 h-4 flex items-center justify-center mt-1" />
                <span>
                  <span className="block text-xs text-foreground-500">内容与投稿</span>
                  <span className="text-foreground-800">hello@qiyoucinema.example</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-briefcase-line text-base text-primary-600 w-4 h-4 flex items-center justify-center mt-1" />
                <span>
                  <span className="block text-xs text-foreground-500">商务合作</span>
                  <span className="text-foreground-800">media@qiyoucinema.example</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-time-line text-base text-primary-600 w-4 h-4 flex items-center justify-center mt-1" />
                <span>
                  <span className="block text-xs text-foreground-500">回复时间</span>
                  <span className="text-foreground-800">工作日 10:00 - 18:00</span>
                </span>
              </li>
            </ul>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-secondary-200">
              {[
                { icon: "ri-weibo-line", label: "微博", href: "https://uxszq.cn" },
                { icon: "ri-wechat-line", label: "微信", href: "https://weixin.qq.com" },
                { icon: "ri-tiktok-line", label: "抖音", href: "https://www.douyin.com" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-8 h-8 rounded-md border border-secondary-300 flex items-center justify-center text-foreground-600 hover:text-accent-700 hover:border-accent-400 transition-colors cursor-pointer"
                >
                  <i className={`${social.icon} text-base w-4 h-4 flex items-center justify-center`} />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {VALUES.map((value) => (
            <div key={value.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center mb-2.5">
                <i className={`${value.icon} text-base w-4 h-4 flex items-center justify-center`} />
              </span>
              <h3 className="text-sm font-semibold text-foreground-950">{value.title}</h3>
              <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{value.desc}</p>
            </div>
          ))}
        </section>

        <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <h2 className="text-lg font-bold text-foreground-950 font-heading mb-2.5">使用声明与版权</h2>
          <div className="space-y-3 text-sm text-foreground-700 leading-[1.9]">
            <p>
              本站所有影片、剧集名称，剧情简介，导演与演员姓名，剧集分集内容，以及资讯报道均为虚构示例，仅用于产品展示，与任何真实作品、机构或人物无关。
            </p>
            <p>
              本站不提供影片在线观看、下载、票务或任何形式的付费内容，站内热度数值为演示排序依据，不代表真实评价或评分。
            </p>
            <p>
              收藏状态通过浏览器本地存储保存，仅在本机有效，不会上传至服务器；清除浏览器数据后收藏记录会一并清空。搜索与筛选均在浏览器端完成，属于演示性质功能。
            </p>
            <p>页面内容与排版版权归奇优影院演示站点所有，如需引用请注明来源。</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link
              to="/category/tv"
              className="h-9 px-3.5 inline-flex items-center rounded-md bg-background-100 text-sm text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              电视剧分类
            </Link>
            <Link
              to="/search"
              className="h-9 px-3.5 inline-flex items-center rounded-md bg-background-100 text-sm text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索影视
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}